import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronDown, Plus, FileText, Folder, Pencil, Trash } from 'lucide-react';
import { useUpdateDoc } from '../../../docs/hooks/useDocsData';

/* eslint-disable react-hooks/set-state-in-effect */

const TAG_PALETTE = [
    { bg: '#ef444420', border: '#ef444440', text: '#ef4444' }, // Red
    { bg: '#f9731620', border: '#f9731640', text: '#f97316' }, // Orange
    { bg: '#eab30820', border: '#eab30840', text: '#eab308' }, // Yellow
    { bg: '#22c55e20', border: '#22c55e40', text: '#22c55e' }, // Green
    { bg: '#14b8a620', border: '#14b8a640', text: '#14b8a6' }, // Teal
    { bg: '#06b6d420', border: '#06b6d440', text: '#06b6d4' }, // Cyan
    { bg: '#3b82f620', border: '#3b82f640', text: '#3b82f6' }, // Blue
    { bg: '#6366f120', border: '#6366f140', text: '#6366f1' }, // Indigo
    { bg: '#8b5cf620', border: '#8b5cf640', text: '#8b5cf6' }, // Violet
    { bg: '#d946ef20', border: '#d946ef40', text: '#d946ef' }, // Fuchsia
    { bg: '#f43f5e20', border: '#f43f5e40', text: '#f43f5e' }, // Rose
    { bg: '#94a3b820', border: '#94a3b840', text: '#94a3b8' }, // Slate
];

// Nodo recursivo del Índice del Cuadro de Trabajo (integración main→dev, epic D1.3).
// Presentacional: recibe el nodo + estado (selección/búsqueda/abierto/DnD) + callbacks.
// Era el `renderNode` interno del `WorkframeIndexColumn` de 810 líneas de `main`.

function highlightMatch(text, term) {
    if (!term || !text) return text;
    const lowerText = text.toLowerCase();
    const index = lowerText.indexOf(term.toLowerCase());
    if (index === -1) return text;
    return (
        <>
            {text.substring(0, index)}
            <mark className="wf-search-highlight">{text.substring(index, index + term.length)}</mark>
            {text.substring(index + term.length)}
        </>
    );
}

function renderAsTags(text, isSearching, term, isEditing, tagColors = {}, onTagClick) {
    if (!text) return null;
    return text.split(/\s+/).filter(Boolean).map((word, idx) => {
        const colorIdx = tagColors[word];
        const color = typeof colorIdx === 'number' && TAG_PALETTE[colorIdx] ? TAG_PALETTE[colorIdx] : null;
        
        const defaultStyle = {
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            color: 'inherit'
        };

        const activeStyle = color ? {
            background: color.bg,
            border: `1px solid ${color.border}`,
            color: color.text
        } : defaultStyle;

        return (
            <span
                key={idx}
                onClick={(e) => {
                    if (isEditing) {
                        e.stopPropagation();
                        onTagClick(word, e.currentTarget);
                    }
                }}
                className={`wf-word-tag ${isEditing ? 'editing' : ''}`}
                style={{
                    display: 'inline-flex',
                    flexShrink: 0,
                    background: activeStyle.background,
                    border: isEditing ? `1px dashed ${color ? color.text : 'rgba(255, 255, 255, 0.5)'}` : activeStyle.border,
                    color: activeStyle.color,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    marginRight: '2px',
                    marginTop: '1px',
                    marginBottom: '1px',
                    fontSize: '0.9em',
                    cursor: isEditing ? 'pointer' : 'default',
                    transition: 'all 0.2s ease'
                }}
            >
                {isSearching ? highlightMatch(word, term.trim()) : word}
            </span>
        );
    });
}

function DocIcon({ icon, type }) {
    if (icon && icon !== 'folder' && icon !== 'file') {
        if (typeof icon === 'string' && (icon.startsWith('http') || icon.startsWith('/') || icon.startsWith('data:'))) {
            return <img src={icon} alt="" style={{ width: '14px', height: '14px', borderRadius: '2px', objectFit: 'cover' }} />;
        }
        return <span>{icon}</span>;
    }
    return type === 'folder'
        ? <Folder size={14} color="#64748b" />
        : <FileText size={14} color="#94a3b8" />;
}

export default function WorkframeIndexNode({
    node,
    depth = 0,
    rootDocId,
    selectedDocId,
    selectedItemRef,
    searchTerm,
    searchState,
    openIds,
    dnd,
    creatingParentId,
    newDocTitle,
    onSelectDoc,
    onToggleOpen,
    onStartCreate,
    onNewDocTitleChange,
    onSubmitCreate,
    onCancelCreate,
    onDeleteDoc,
    isEditingTagsMode,
    t,
}) {
    const updateDoc = useUpdateDoc();
    const [colorPickerTarget, setColorPickerTarget] = useState(null);
    const [localTagColors, setLocalTagColors] = useState(node.tag_colors || {});
    const pickerRef = useRef(null);

    useEffect(() => {
        setLocalTagColors(node.tag_colors || {});
    }, [node.tag_colors]);

    useEffect(() => {
        if (!colorPickerTarget) return undefined;
        const handleClickOutside = (e) => {
            if (pickerRef.current && pickerRef.current.contains(e.target)) return;
            setColorPickerTarget(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [colorPickerTarget]);

    const handleTagClick = (word, element) => {
        const rect = element.getBoundingClientRect();
        setColorPickerTarget({ word, top: rect.bottom + 4, left: rect.left });
    };

    const handleColorSelect = async (colorIdx) => {
        if (!colorPickerTarget) return;
        const { word } = colorPickerTarget;
        const newColors = { ...localTagColors, [word]: colorIdx };
        
        setLocalTagColors(newColors);
        setColorPickerTarget(null);
        
        try {
            await updateDoc.mutateAsync({ id: node.id, payload: { tag_colors: newColors } });
        } catch {
            setLocalTagColors(localTagColors); // revert on error
        }
    };

    if (searchState.isSearching && !searchState.visibleIds.has(node.id)) return null;

    const isSearchExpanded = searchState.isSearching && searchState.expandedIds.has(node.id);
    const isOpen = isSearchExpanded || openIds.has(node.id);
    const isSelected = selectedDocId === node.id;
    const isDirectMatch = searchState.isSearching && searchState.matchingIds.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isRoot = String(node.id) === String(rootDocId);
    const isDragging = String(dnd.draggingId) === String(node.id);
    const isDragOver = String(dnd.dragOverId) === String(node.id);
    const displayName = node.name || t('workframe.index.untitled');

    return (
        <div className="wf-node-container">
            <div
                ref={isSelected ? selectedItemRef : null}
                className={[
                    'wf-doc-item',
                    isSelected ? 'selected' : '',
                    isDirectMatch ? 'search-matched' : '',
                    isDragging ? 'dragging' : '',
                    isDragOver && dnd.dropPosition === 'top' ? 'drop-target-top' : '',
                    isDragOver && dnd.dropPosition === 'bottom' ? 'drop-target-bottom' : '',
                    isDragOver && dnd.dropPosition === 'inside' ? 'drop-target-inside' : '',
                ].filter(Boolean).join(' ')}
                style={{ 
                    paddingLeft: `${depth * 14 + 10}px`,
                    top: `${depth * 30 - 8}px`,
                    zIndex: 100 - depth
                }}
                onClick={() => onSelectDoc(node.id, node.nodeNumber)}
                draggable={!isRoot}
                onDragStart={(e) => dnd.handleDragStart(e, node)}
                onDragEnd={dnd.handleDragEnd}
                onDragOver={(e) => dnd.handleDragOver(e, node)}
                onDragLeave={(e) => dnd.handleDragLeave(e, node)}
                onDrop={(e) => dnd.handleDrop(e, node)}
            >
                <span
                    className={`wf-chevron ${hasChildren ? 'visible' : 'hidden'}`}
                    onClick={(e) => { e.stopPropagation(); onToggleOpen(node.id); }}
                >
                    {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                </span>

                <span className="wf-doc-icon">
                    <DocIcon icon={node.icon} type={node.type} />
                </span>

                <span className="wf-doc-name" title={displayName} style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', overflowX: 'auto', scrollbarWidth: 'none' }}>
                    {node.nodeNumber && (
                        <span style={{ opacity: 0.5, marginRight: '4px', fontSize: '0.9em', fontWeight: '500', flexShrink: 0 }}>{node.nodeNumber}</span>
                    )}
                    {renderAsTags(displayName, searchState.isSearching, searchTerm, isEditingTagsMode, localTagColors, handleTagClick)}
                </span>

                <div className="wf-doc-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                        className="wf-action-btn"
                        title={t('workframe.index.deleteDoc', 'Eliminar documento')}
                        onClick={(e) => { e.stopPropagation(); onDeleteDoc?.(node.id, displayName); }}
                    >
                        <Trash size={13} color="#ef4444" />
                    </button>
                    <button
                        className="wf-action-btn"
                        title={t('workframe.index.addSubdoc')}
                        onClick={(e) => { e.stopPropagation(); onStartCreate(node.id); }}
                    >
                        <Plus size={13} />
                    </button>
                </div>
            </div>

            {creatingParentId === node.id && (
                <div className="wf-doc-inline-create" style={{ paddingLeft: `${(depth + 1) * 14 + 10}px` }}>
                    <FileText size={13} color="#94a3b8" />
                    <input
                        type="text"
                        placeholder={t('workframe.index.subdocPlaceholder')}
                        value={newDocTitle}
                        onChange={(e) => onNewDocTitleChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') onSubmitCreate(node.id);
                            if (e.key === 'Escape') onCancelCreate();
                        }}
                        autoFocus
                    />
                    <button className="wf-inline-save-btn" onClick={() => onSubmitCreate(node.id)}>
                        {t('workframe.index.create')}
                    </button>
                </div>
            )}

            {isOpen && hasChildren && (
                <div className="wf-children" style={{ '--tree-depth': depth }}>
                    {node.children.map((child) => (
                        <WorkframeIndexNode
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            rootDocId={rootDocId}
                            selectedDocId={selectedDocId}
                            selectedItemRef={selectedItemRef}
                            searchTerm={searchTerm}
                            searchState={searchState}
                            openIds={openIds}
                            dnd={dnd}
                            creatingParentId={creatingParentId}
                            newDocTitle={newDocTitle}
                            onSelectDoc={onSelectDoc}
                            onToggleOpen={onToggleOpen}
                            onStartCreate={onStartCreate}
                            onNewDocTitleChange={onNewDocTitleChange}
                            onSubmitCreate={onSubmitCreate}
                            onCancelCreate={onCancelCreate}
                            onDeleteDoc={onDeleteDoc}
                            isEditingTagsMode={isEditingTagsMode}
                            t={t}
                        />
                    ))}
                </div>
            )}

            {colorPickerTarget && (
                <div
                    ref={pickerRef}
                    style={{
                        position: 'fixed',
                        top: `${colorPickerTarget.top}px`,
                        left: `${colorPickerTarget.left}px`,
                        zIndex: 10000,
                        background: '#1a1a1a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '8px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '6px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                    }}
                >
                    {TAG_PALETTE.map((color, idx) => (
                        <div
                            key={idx}
                            onClick={(e) => { e.stopPropagation(); handleColorSelect(idx); }}
                            style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                background: color.bg,
                                border: `1px solid ${color.border}`,
                                cursor: 'pointer',
                            }}
                            title={`Color ${idx + 1}`}
                        />
                    ))}
                    <div
                        onClick={(e) => { e.stopPropagation(); handleColorSelect(null); }}
                        style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.08)',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            color: '#666'
                        }}
                        title="Limpiar color"
                    >
                        ✕
                    </div>
                </div>
            )}
        </div>
    );
}
